import { ref, watch } from 'vue';
import { PaginationData } from '@src/pagination/interfaces/pagination-data.js';
import { useRouterComposable } from '@router/router-composable.ts';

export function usePaginationComposable<Type>(loadFunction: (currentPage: number) => Promise<PaginationData<Type>>, immediate = true, withRouteChange = true) {
    const data = ref<Array<Type>>([]);

    const { currentRoute, pushParams } = useRouterComposable();

    let pageOnRoute = 1;
    if (currentRoute.value.query?.has('page')) {
        pageOnRoute = Number.parseInt(currentRoute.value.query?.get('page') as string);
    }

    const currentPage = ref<number>(pageOnRoute);
    const maxPage = ref<number>(1);
    const loading = ref<boolean>(false);

    async function load(disableLoadingFlag = false) {
        if (!disableLoadingFlag) {
            loading.value = true;
        }
        try {
            const result = await loadFunction(currentPage.value);
            if (result === undefined) {
                return;
            }
            loading.value = false;
            data.value = result.data;

            // falls wir keine Ergebnisse zurückbekommen, setzen wir die currentPage auf die letzte Seite und probieren es erneut
            if (result.data.length === 0 && currentPage.value > 1) {
                currentPage.value = result.pageCount > 0 ? result.pageCount : 1;
                // wenn wir keine Ergebnisse haben ist pageCount 0
                if (result.pageCount > 0) {
                    await load();
                    return;
                }
            }

            maxPage.value = result.pageCount === 0 ? 1 : result.pageCount;
        } catch (e: any) {
            loading.value = false;
            throw e;
        }
    }

    if (immediate) {
        load();
    }

    watch(currentPage, () => {
        if (withRouteChange) {
            pushParams({
                page: currentPage.value,
            });
        }
        load();
    });

    watch(maxPage, () => {
        if (currentPage.value > maxPage.value) {
            currentPage.value = maxPage.value;
        }
    });

    return {
        data,
        currentPage,
        maxPage,
        loading,
        load,
    };
}
